import { Activity, AlertTriangle, Database, Server, ShieldAlert } from 'lucide-react';

import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';
import type { CacheMetrics } from '@/types/monitoring';

export interface CacheDetailsPanelProps {
  metrics: CacheMetrics | null;
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function CacheDetailsPanel({ metrics }: CacheDetailsPanelProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.cacheDetails;
  const tCache = messages.monitoring.cache;

  if (!metrics) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">{t.title}</h3>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Database className="w-12 h-12 mx-auto mb-2" />
          <p>{tCache.unavailable}</p>
        </div>
      </Card>
    );
  }

  const details = [
    {
      label: t.totalRequests,
      value: metrics.total_requests,
      icon: Database,
      alert: false,
    },
    {
      label: t.errors,
      value: metrics.errors,
      icon: AlertTriangle,
      alert: metrics.errors > 0,
    },
    {
      label: t.redisErrors,
      value: metrics.redis_errors,
      icon: AlertTriangle,
      alert: metrics.redis_errors > 0,
    },
    {
      label: t.upstreamRequests,
      value: metrics.upstream_requests,
      icon: Server,
      alert: false,
    },
    {
      label: t.upstreamErrors,
      value: metrics.upstream_errors,
      icon: ShieldAlert,
      alert: metrics.upstream_errors > 0,
    },
    {
      label: t.activeUpstreamRequests,
      value: metrics.active_upstream_requests,
      icon: Activity,
      alert: false,
    },
    {
      label: t.vectorContractFailures,
      value: metrics.vector_contract_failures,
      icon: ShieldAlert,
      alert: metrics.vector_contract_failures > 0,
    },
  ];

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t.title}</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {details.map(({ label, value, icon: Icon, alert }) => (
          <div key={label} className="bg-surface-2 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-4 h-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
            <p className={cn('text-xl font-bold', alert ? 'text-destructive' : 'text-foreground')}>
              {formatNumber(value)}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}
